import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  School as SchoolIcon,
  Book as BookIcon,
  CreditCard as CreditCardIcon,
  Campaign as CampaignIcon,
  Mail as MailIcon,
  Storage as StorageIcon,
  ChildCare as ChildCareIcon,
  Edit as EditIcon,
  BarChart as BarChartIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const ICONE_MAP = {
  settings: <SettingsIcon />,
  'admin-panel': <SettingsIcon />,
  school: <SchoolIcon />,
  book: <BookIcon />,
  edit: <EditIcon />,
  'credit-card': <CreditCardIcon />,
  'message-circle': <CampaignIcon />,
  mail: <MailIcon />,
  storage: <StorageIcon />,
  baby: <ChildCareIcon />,
  chart: <BarChartIcon />,
};

const GestaoModulos = ({ instituicaoId }) => {
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/modulos/instituicao/${instituicaoId}`);
      setModulos(data);
    } catch (err) {
      setFeedback({ type: 'error', msg: 'Erro ao carregar módulos.' });
    } finally {
      setLoading(false);
    }
  }, [instituicaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const toggleModulo = async (modulo) => {
    if (modulo.modulo_base) return;
    const novoEstado = !modulo.instituicao_ativo;
    setSalvando(modulo.id);
    setFeedback(null);
    try {
      await api.post(`/modulos/instituicao/${instituicaoId}/modulo/${modulo.id}`, {
        ativo: novoEstado,
      });
      setModulos((prev) =>
        prev.map((m) =>
          m.id === modulo.id ? { ...m, instituicao_ativo: novoEstado } : m
        )
      );
      setFeedback({
        type: 'success',
        msg: `Módulo "${modulo.nome}" ${novoEstado ? 'activado' : 'desactivado'} com sucesso.`,
      });
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao actualizar módulo.';
      setFeedback({ type: 'error', msg });
    } finally {
      setSalvando(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Módulos da Instituição
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Activar ou desactivar os módulos disponíveis para esta instituição.
        Módulos base não podem ser desactivados.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.type}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      <Grid container spacing={2}>
        {modulos.map((modulo) => {
          const isBase = !!modulo.modulo_base;
          const isAtivo = isBase || !!modulo.instituicao_ativo;
          const isSalvando = salvando === modulo.id;
          const deps = Array.isArray(modulo.dependencias) ? modulo.dependencias : [];

          return (
            <Grid item xs={12} sm={6} md={4} key={modulo.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: isAtivo ? 'primary.light' : 'divider',
                  bgcolor: isAtivo ? 'primary.50' : 'background.paper',
                  opacity: isBase ? 0.75 : 1,
                  transition: 'all 0.2s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: isAtivo ? 'primary.main' : 'action.disabledBackground',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isAtivo ? 'white' : 'text.disabled',
                        flexShrink: 0,
                      }}
                    >
                      {ICONE_MAP[modulo.icone] || <SettingsIcon />}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} lineHeight={1.3}>
                        {modulo.nome}
                      </Typography>
                      {isBase && (
                        <Chip
                          label="Base"
                          size="small"
                          icon={<LockIcon sx={{ fontSize: '12px !important' }} />}
                          sx={{ height: 18, fontSize: '0.6rem', mt: 0.25 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {isSalvando ? (
                    <CircularProgress size={22} sx={{ mt: 0.5 }} />
                  ) : (
                    <Tooltip title={isBase ? 'Módulo obrigatório' : (isAtivo ? 'Desactivar' : 'Activar')}>
                      <span>
                        <Switch
                          checked={isAtivo}
                          onChange={() => toggleModulo(modulo)}
                          disabled={isBase}
                          size="small"
                          color="primary"
                        />
                      </span>
                    </Tooltip>
                  )}
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1.5, lineHeight: 1.4 }}
                >
                  {modulo.descricao}
                </Typography>

                {deps.length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>
                        Requer:
                      </Typography>
                      {deps.map((d) => (
                        <Chip key={d} label={d} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                      ))}
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

GestaoModulos.propTypes = {
  instituicaoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default GestaoModulos;
